import React, { Suspense } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useAuth } from 'contexts/AuthContext';

import ActiveProjectsDashboard from 'components/dashboard/ActiveProjectsDashboard';
import { ScreenKey } from 'constant/ScreenPermission';
import { Role } from 'stores/AuthenticationStoreAdapter';

const UserNotice = React.lazy(() => import('components/notice/UserNotice'));

// --- メニュー定義 ---

type MenuItem = {
  path: string;
  icon: React.ReactNode;
  title: string;
  color: string;
  /** 表示に必要な画面キー。未指定なら常時表示 */
  screenKey?: ScreenKey;
};

type MenuGroup = {
  label: string;
  items: MenuItem[];
};

const menuGroups: MenuGroup[] = [
  {
    label: '業務',
    items: [
      {
        path: '/member',
        icon: <PeopleIcon />,
        title: '要員管理',
        color: '#1565c0',
        screenKey: ScreenKey.MEMBER_TOP,
      },
      {
        path: '/contract',
        icon: <DescriptionIcon />,
        title: '契約管理',
        color: '#6a1b9a',
        screenKey: ScreenKey.CONTRACT_TOP,
      },
      {
        path: '/assessment',
        icon: <InventoryIcon />,
        title: '資産管理',
        color: '#283593',
        screenKey: ScreenKey.ASSESSMENT_TOP,
      },
      {
        path: '/expense',
        icon: <ReceiptLongIcon />,
        title: '経費管理',
        color: '#ef6c00',
        screenKey: ScreenKey.EXPENSE_TOP,
      },
      {
        path: '/security',
        icon: <SecurityIcon />,
        title: 'セキュリティ',
        color: '#2e7d32',
        screenKey: ScreenKey.SECURITY_TOP,
      },
      {
        path: '/ticket',
        icon: <ConfirmationNumberIcon />,
        title: 'チケット管理',
        color: '#c2185b',
        screenKey: ScreenKey.TICKET_TOP,
      },
    ],
  },
  {
    label: '学習',
    items: [
      {
        path: '/workspace',
        icon: <SchoolIcon />,
        title: 'ワークスペース',
        color: '#00838f',
        screenKey: ScreenKey.TRAINING_TOP,
      },
      {
        path: '/training-learn',
        icon: <ModelTrainingIcon />,
        title: '研修',
        color: '#0097a7',
        screenKey: ScreenKey.TRAINING_LEARN_TOP,
      },
      {
        path: '/training-admin',
        icon: <TuneIcon />,
        title: '研修管理',
        color: '#00695c',
        screenKey: ScreenKey.TRAINING_ADMIN_TOP,
      },
    ],
  },
  {
    label: 'その他',
    items: [
      {
        path: '/devdocs',
        icon: <ArticleIcon />,
        title: 'Dev Insights',
        color: '#1e3a8a',
      },
      {
        path: '/management',
        icon: <AdminPanelSettingsIcon />,
        title: '管理者メニュー',
        color: '#00695c',
        screenKey: ScreenKey.MGMT_TOP,
      },
      {
        path: '/guide',
        icon: <MenuBookIcon />,
        title: '利用ガイド',
        color: '#e65100',
      },
    ],
  },
];

const Home: React.FC = () => {
  const { userSession } = useAuth();
  const { allowedScreens } = userSession;
  const navigate = useNavigate();

  const isManager = userSession.roles.includes('MANAGER' as Role);

  // 権限でフィルタし、項目が残るグループだけ表示する
  const visibleGroups = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.screenKey || allowedScreens.includes(item.screenKey),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <Stack spacing={2.5}>
      {/* ウェルカムヘッダー（1行・コンパクト） */}
      <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>
          ダッシュボード
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {userSession.userName} さん、お疲れ様です
        </Typography>
      </Stack>

      {/* メニュー（グループ別・横並びコンパクトタイル） */}
      <Stack spacing={1.5}>
        {visibleGroups.map((group) => (
          <Box key={group.label}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: 'text.secondary',
              }}
            >
              {group.label}
            </Typography>
            <Grid container spacing={1}>
              {group.items.map((item) => (
                <Grid key={item.path} size={{ xs: 6, sm: 4, md: 3 }}>
                  <MenuTile
                    onClick={() => navigate(item.path)}
                    icon={item.icon}
                    title={item.title}
                    color={item.color}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* 通知 */}
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          通知
        </Typography>
        <Suspense fallback={<LinearProgress />}>
          <UserNotice />
        </Suspense>
      </Box>

      {/* 課長ダッシュボード: プロジェクト一覧 */}
      {isManager && (
        <Box sx={{ width: 520 }}>
          <ActiveProjectsDashboard managerId={userSession.userId} />
        </Box>
      )}
    </Stack>
  );
};

type MenuTileProps = {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  color: string;
};

function MenuTile({ onClick, icon, title, color }: MenuTileProps) {
  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: color,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ px: 1.25, py: 1 }}>
        {/* アイコンとタイトルを横並びにして縦幅を抑える */}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}14`,
              color: color,
            }}
          >
            {React.cloneElement(icon as React.ReactElement<{ sx?: object }>, {
              sx: { fontSize: 18 },
            })}
          </Box>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ color: 'text.primary', minWidth: 0 }}
          >
            {title}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default Home;
